import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'
import { watchEffect, watch } from 'vue'

// Helper to validate if a string is a valid UUID
const isValidUUID = (str) => {
  if (!str || typeof str !== 'string') return false
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

// Helper to generate domain-aware invite URLs
// Uses current domain for localhost development, otherwise uses production URL
const buildInviteUrl = (inviteCode) => {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')

  const baseUrl = isLocalhost
    ? `${window.location.protocol}//${window.location.host}`
    : 'https://spectra.reviews'

  return `${baseUrl}/~/lists/join/${inviteCode}`
}

export const useStoreLists = defineStore('storeLists', {
  persist: {
    // Only persist the actual data, not temporary session state
    pick: ['lists', 'listsFollowing', 'items', 'loaded'],
    serializer: {
      serialize: JSON.stringify,
      deserialize: JSON.parse
    }
  },
  state: () => {
    return {
      lists: [],
      listsFollowing: [],
      loaded: false,
      items: [],
      initReady: false,
      currentUserId: null,
      initFailed: false, // Track if initialization failed (e.g., deleted user)

      paginatedCursors: {},
      listsPaginationState: {}, // Tracks loading state, hasMore for each list
      listErrors: {}, // Track errors per list to prevent infinite retries

      // Search state
      searchResults: {
        byName: [],
        byTitle: []
      },
      searchQuery: '',
      searchLoading: false,
      searchTotalCount: 0,

      // Title lists state (lists containing a specific title)
      titleLists: {},
      titleListsLoading: {},

      // Collaborative lists state
      collaborativeListMembers: {}, // { [listId]: members[] }
      listActivity: {}, // { [listId]: activities[] }
      listsJoinedAsMember: [], // Lists where current user is member (not owner)

      // Invite state
      listInviteLinks: {}, // { [listId]: invites[] } - shareable invite links
      listUserInvites: {}, // { [listId]: userInvites[] } - direct user invites
      pendingReceivedInvites: [] // Invites sent to current user
    }
  },
  actions: {
    async init() {
      const storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        console.log('Skipping lists initialization - user not authenticated')
        return
      }

      this.currentUserId = storeAuth.user.id
      this.initReady = true
      this.fetchUserSystemLists(this.currentUserId)
      this.fetchUserLists(this.currentUserId)
      this.fetchJoinedLists() // Fetch collaborative lists user has joined
      this.subscribeToChanges(this.currentUserId)
    },

    // Refresh store after app resumes from background
    async refreshAfterResume() {
      const storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        console.log(
          '[storeLists] Skipping refresh after resume - user not authenticated'
        )
        return
      }

      console.log('[storeLists] Refreshing after app resume')

      // Unsubscribe from old channels (they may be stale/disconnected)
      this.unsubscribeFromChanges()

      // Re-fetch user data
      await this.fetchUserSystemLists(this.currentUserId)
      await this.fetchUserLists(this.currentUserId)
      await this.fetchJoinedLists() // Re-fetch joined collaborative lists

      // Re-subscribe to realtime changes
      this.subscribeToChanges(this.currentUserId)
    },

    // Unsubscribe from all realtime channels
    unsubscribeFromChanges() {
      try {
        supabase.removeChannel(supabase.channel('public:lists'))
        supabase.removeChannel(supabase.channel('public:list_items'))
      } catch (e) {
        // Channels may not exist, that's ok
        console.log('[storeLists] No existing channels to remove')
      }
    },

    // Subscribe to real-time changes for the current user's lists
    subscribeToChanges(userId) {
      supabase
        .channel('public:lists')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'lists',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            // Only add if not already present (avoids duplicate when
            // addList() already pushed the list from the API response)
            if (!this.lists.some((l) => l.id === payload.new.id)) {
              this.lists.push(payload.new)
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'lists',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            const index = this.lists.findIndex(
              (list) => list.id === payload.old.id
            )
            if (index !== -1) {
              this.lists[index] = payload.new // Update existing list in state
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'lists',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            this.removeList(payload.old.id)
            // this.lists = this.lists.filter((list) => list.id !== payload.old.id) // Remove deleted list
          }
        )
        .subscribe()

      supabase
        .channel('public:list_items')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'list_items',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            this.fetchListItemById(payload.new.id)
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'list_items',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            this.fetchListItemById(payload.new.id)
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'list_items',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            this.removeItem(payload.old.id)
            // this.lists = this.lists.filter((list) => list.id !== payload.old.id) // Remove deleted list
          }
        )
        .subscribe()
    },

    updateItems(fetchedItems) {
      // Create a map from the existing lists array for quick lookup
      const itemsMap = new Map(this.items.map((item) => [item.id, item]))
      fetchedItems.forEach((fetchedItem) => {
        if (itemsMap.has(fetchedItem.id)) {
          // Update the existing list
          Object.assign(itemsMap.get(fetchedItem.id), fetchedItem)
        } else {
          // Insert the new items
          itemsMap.set(fetchedItem.id, fetchedItem)
        }
      })
      // Update the lists state with the updated array
      this.items = Array.from(itemsMap.values())
    },

    removeList(listId) {
      this.lists = this.lists.filter((list) => list.id !== listId)
    },

    removeItem(itemId) {
      this.items = this.items.filter((item) => item.id !== itemId)
    },

    async removeItemFromList(listId, itemId) {
      this.removeItem(itemId)

      const path = `lists/${listId}/items/${itemId}/delete`
      const { error } = await supabase.functions.invoke(path, { body: {} })

      if (error) {
        console.error('Error calling lists function', error.message)
        throw error
      }

      this.fetchUserListMeta(listId, this.currentUserId)
      return true
    },

    async reorderItemsInList(listId, newOrder) {
      // this.removeItem(itemId)

      const path = `lists/${listId}/reorder`
      const { error } = await supabase.functions.invoke(path, {
        body: { newOrder: newOrder }
      })

      if (error) {
        console.error('Error calling lists function', error.message)
        throw error
      }

      // return true
      return true
    },

    // Restore items to a previous order (for cancel edit mode)
    restoreItemsOrder(listId, originalItems) {
      if (!listId || !originalItems) return
      this.listItems[listId] = [...originalItems]
    },

    updateLists(fetchedLists) {
      // Create a map from the existing lists array for quick lookup
      const listsMap = new Map(this.lists.map((list) => [list.id, list]))

      fetchedLists.forEach((fetchedList) => {
        if (listsMap.has(fetchedList.id)) {
          // Update the existing list
          Object.assign(listsMap.get(fetchedList.id), fetchedList)
        } else {
          // Insert the new list
          listsMap.set(fetchedList.id, fetchedList)
        }
      })

      // Update the lists state with the updated array
      this.lists = Array.from(listsMap.values())
    },

    updateListsFollowing(fetchedLists) {
      // Create a map from the existing lists array for quick lookup
      const listsMap = new Map(
        this.listsFollowing.map((list) => [list.id, list])
      )

      fetchedLists.forEach((fetchedList) => {
        if (listsMap.has(fetchedList.id)) {
          // Update the existing list
          Object.assign(listsMap.get(fetchedList.id), fetchedList)
        } else {
          // Insert the new list
          listsMap.set(fetchedList.id, fetchedList)
        }
      })

      // Update the lists state with the updated array
      this.listsFollowing = Array.from(listsMap.values())
    },

    // Fetch highlighted lists (promoted lists with fallback to public lists)
    // Supports pagination and returns { lists, total, hasMore, isUsingFallback }
    async fetchHighlightedLists(limit = 10, offset = 0) {
      const selectFields = `
        id, title, description, slug, user_id, club_id, created_at, updated_at, is_promoted, promoted_at, backdrop_path,
        profiles:user_id (username, displayname),
        list_items(count),
        clubs:club_id(id, name, slug, cover_url)
      `

      const formatLists = (data) =>
        data
          ?.filter((list) => {
            const itemCount = list.list_items[0]?.count || 0
            return itemCount > 0
          })
          .map((list) => ({
            ...list,
            owner:
              list.profiles?.displayname ||
              list.profiles?.username ||
              'Unknown',
            itemCount: list.list_items[0]?.count || 0
          })) || []

      try {
        // First, try to fetch promoted lists
        const {
          data: promotedData,
          error: promotedError,
          count: promotedCount
        } = await supabase
          .from('lists')
          .select(selectFields, { count: 'exact' })
          .eq('is_promoted', true)
          .eq('is_private', false)
          .eq('is_system', false)
          .eq('is_auto', false)
          .order('promoted_at', { ascending: false })
          .range(offset, offset + limit - 1)

        if (promotedError) throw promotedError

        // If we have promoted lists, return them
        if (promotedData && promotedData.length > 0) {
          return {
            lists: formatLists(promotedData),
            total: promotedCount || 0,
            hasMore: offset + limit < (promotedCount || 0),
            isUsingFallback: false
          }
        }

        // Fallback: fetch public lists from all users
        const {
          data: fallbackData,
          error: fallbackError,
          count: fallbackCount
        } = await supabase
          .from('lists')
          .select(selectFields, { count: 'exact' })
          .eq('is_private', false)
          .eq('is_system', false)
          .eq('is_auto', false)
          .order('updated_at', { ascending: false })
          .range(offset, offset + limit - 1)

        if (fallbackError) throw fallbackError

        return {
          lists: formatLists(fallbackData),
          total: fallbackCount || 0,
          hasMore: offset + limit < (fallbackCount || 0),
          isUsingFallback: true
        }
      } catch (error) {
        console.error('Failed to fetch highlighted lists:', error)
        return { lists: [], total: 0, hasMore: false, isUsingFallback: false }
      }
    },

    async fetchSystemListByTypeAndUser(type, userId, force = false) {
      // Guard: Validate type parameter
      if (!type) {
        console.warn('fetchSystemListByTypeAndUser called with undefined type')
        return null
      }

      // Guard: Validate userId parameter
      if (!userId) {
        console.warn(
          'fetchSystemListByTypeAndUser called with undefined userId'
        )
        return null
      }

      const stubId = `system-${type}`

      // Guard: Check if this type has already errored (unless forcing)
      if (this.listErrors[stubId]?.permanent) {
        if (!force) {
          return null
        }
        // Clear error state when forcing a refresh
        delete this.listErrors[stubId]
      }

      // Check if the list exists already in the state
      const existingList = this.lists.find(
        (list) =>
          list.is_system === true &&
          list.system_type === type &&
          list.user_id === userId &&
          list.is_stub !== true
      )
      if (existingList && !force) {
        return existingList
      }
      // Create a stub list for the system list too prevent multiple requests
      if (!existingList) {
        this.lists.push({
          id: stubId,
          is_stub: true,
          is_system: true,
          system_type: type,
          user_id: userId,
          loading: true
        })
      } else {
        existingList.loading = true
        existingList.error = null
      }

      const { data, error } = await supabase
        .from('lists')
        .select('*, profiles!fk_lists_profiles_user_id(*), list_items(count), list_review_stats(*)')
        .eq('is_system', true)
        .eq('system_type', type)
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle()

      if (error) {
        // Remove the stub on error to prevent it from causing issues
        this.removeList(stubId)
        // Mark this type as having an error to prevent infinite retries
        this.listErrors[stubId] = { permanent: true, error }
        console.warn(`Failed to fetch system list ${type}:`, error.message)
        return null
      }

      if (!data) {
        // No list found for this type - remove stub, don't mark as permanent error
        this.removeList(stubId)
        return null
      }

      data.loading = false

      this.updateLists([data])
      // Kill the stub
      this.removeList(stubId)

      return data
    },

    async fetchSystemListByType(type, force = false) {
      // Guard: Validate type parameter
      if (!type) {
        console.warn('fetchSystemListByType called with undefined type')
        return null
      }

      if (!this.currentUserId) {
        return new Promise((resolve) => {
          const stopWatching = watch(
            () => this.currentUserId,
            async (newVal, oldVal) => {
              if (newVal && newVal !== oldVal) {
                stopWatching() // Stop watching after first trigger
                const result = await this.fetchSystemListByTypeAndUser(
                  type,
                  this.currentUserId,
                  force
                )
                resolve(result)
              }
            }
          )
          // Add timeout to prevent indefinite waiting
          setTimeout(() => {
            stopWatching()
            resolve(null)
          }, 10000)
        })
      } else {
        return this.fetchSystemListByTypeAndUser(
          type,
          this.currentUserId,
          force
        )
      }
    },

    async fetchListItemsByListIdPaginatedNext(listId) {
      // Guard: Validate listId is a proper UUID
      if (!isValidUUID(listId)) {
        console.warn(
          'Invalid list ID, skipping fetchListItemsByListIdPaginatedNext:',
          listId
        )
        return
      }

      // Guard: Check if this list has had a permanent error
      if (this.listErrors[listId]?.permanent) {
        return
      }

      // Find the paginated cursor for the list
      const cursor = this.paginatedCursors[listId]

      // Set loading state
      if (!this.listsPaginationState[listId]) {
        this.listsPaginationState[listId] = { loading: false, hasMore: true }
      }

      // If already loading or no more items, return
      if (
        this.listsPaginationState[listId].loading ||
        !this.listsPaginationState[listId].hasMore
      ) {
        return
      }

      // Set loading state to true
      this.listsPaginationState[listId].loading = true

      if (!cursor) {
        // Fetch the first page
        await this.fetchListItemsByListIdPaginated(listId)
        return
      }

      // Got the cursor, fetch the next page via fetchListItemsByListIdPaginated
      const limit = 10
      const offset = cursor.offset + limit
      await this.fetchListItemsByListIdPaginated(listId, limit, offset)

      return
    },

    async fetchListItemsByListIdPaginated(listId, limit = 10, offset = 0) {
      // Guard: Validate listId is a proper UUID
      if (!isValidUUID(listId)) {
        console.warn('Invalid list ID, skipping fetch:', listId)
        return
      }

      // Guard: Check if this list has had a permanent error
      if (this.listErrors[listId]?.permanent) {
        console.warn('List has permanent error, skipping fetch:', listId)
        return
      }

      // Initialize pagination state if not exists
      if (!this.listsPaginationState[listId]) {
        this.listsPaginationState[listId] = { loading: true, hasMore: true }
      } else {
        this.listsPaginationState[listId].loading = true
      }

      const { data, error } = await supabase
        .from('list_items')
        .select(
          `*,titles!list_items_title_id_fkey(*), added_by_profile:profiles!list_items_added_by_profiles_fk(*), reviews(*, profiles(*)), notes(*, profiles:profiles!notes_user_id_profiles_fk(*))`
        )
        .eq('list_id', listId)
        .order('order_num', { ascending: true })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) {
        console.error('Error fetching list items with titles:', error)
        this.listsPaginationState[listId].loading = false
        // Mark as permanent error if it's a 400 (bad request) to prevent retries
        if (error.code === '22P02' || error.code === '400') {
          this.listErrors[listId] = { permanent: true, error }
        }
        return
      }

      // Update the paginated cursor for the list
      this.paginatedCursors[listId] = { limit: limit, offset: offset }

      // Check if we have more items
      // If we got fewer items than the limit, we've reached the end
      const hasMore = data.length === limit

      // Also check against the total count if available
      const list = this.lists.find((l) => l.id === listId)
      if (list && list.list_items && list.list_items[0]?.count !== undefined) {
        const totalCount = list.list_items[0].count
        const currentItemsCount = this.getListItemsByListId(listId).length
        // If we've loaded all items, there are no more
        if (currentItemsCount >= totalCount) {
          this.listsPaginationState[listId].hasMore = false
        } else {
          this.listsPaginationState[listId].hasMore = hasMore
        }
      } else {
        // Fallback to simple check
        this.listsPaginationState[listId].hasMore = hasMore
      }

      this.listsPaginationState[listId].loading = false

      this.updateItems(data)
      return
    },

    /**
     * Auto-load list items up to a maximum count (default 100)
     * This will continuously fetch pages until the limit is reached or no more items exist
     * @param {string} listId - The list ID to load items for
     * @param {number} maxItems - Maximum number of items to load (default 100)
     * @param {number} pageSize - Number of items per page (default 20)
     */
    async autoLoadListItemsUpTo(listId, maxItems = 100, pageSize = 20) {
      // Guard: Validate listId is a proper UUID
      if (!isValidUUID(listId)) {
        console.warn('Invalid list ID, skipping autoLoadListItemsUpTo:', listId)
        return
      }

      // Guard: Check if this list has had a permanent error
      if (this.listErrors[listId]?.permanent) {
        return
      }

      // Get current loaded count
      const currentCount = this.getListItemsByListId(listId).length

      // If we already have enough items, no need to load more
      if (currentCount >= maxItems) {
        return
      }

      // Get total count from list metadata if available
      const list = this.lists.find((l) => l.id === listId)
      const totalCount = list?.list_items?.[0]?.count || Infinity

      // If we already have all items, no need to load more
      if (currentCount >= totalCount) {
        return
      }

      // Check pagination state - if already loading, wait
      const paginationState = this.listsPaginationState[listId]
      if (paginationState?.loading) {
        return
      }

      // If there are no more items to load, exit
      if (paginationState?.hasMore === false) {
        return
      }

      // Determine how many items we still need
      const itemsNeeded = Math.min(maxItems, totalCount) - currentCount

      // Calculate how many pages we need to load
      const pagesToLoad = Math.ceil(itemsNeeded / pageSize)

      // Load pages sequentially until we have enough or run out
      for (let i = 0; i < pagesToLoad; i++) {
        // Re-check current count after each load
        const loadedCount = this.getListItemsByListId(listId).length
        if (loadedCount >= maxItems) {
          break
        }

        // Check if there are more items
        const currentState = this.listsPaginationState[listId]
        if (currentState?.hasMore === false) {
          break
        }

        // Determine offset - use the actual loaded count to ensure we don't skip items
        // This handles cases where different page sizes were used previously
        const offset = loadedCount

        // Fetch next page
        await this.fetchListItemsByListIdPaginated(listId, pageSize, offset)
      }
    },

    async fetchAllListItemsById(listId) {
      // Guard: Validate listId is a proper UUID
      if (!isValidUUID(listId)) {
        console.warn('Invalid list ID, skipping fetchAllListItemsById:', listId)
        return
      }

      // Guard: Check if this list has had a permanent error
      if (this.listErrors[listId]?.permanent) {
        return
      }

      const { data, error } = await supabase
        .from('list_items')
        .select(
          `*,titles!list_items_title_id_fkey(*), added_by_profile:profiles!list_items_added_by_profiles_fk(*), reviews(*, profiles(*)), notes(*, profiles:profiles!notes_user_id_profiles_fk(*))`
        )
        .eq('list_id', listId)
        .order('order_num', { ascending: true })
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) {
        console.error('Error fetching all list items:', error)
        // Mark as permanent error if it's a bad request
        if (error.code === '22P02' || error.code === '400') {
          this.listErrors[listId] = { permanent: true, error }
        }
        return
      }

      this.updateItems(data)
      return
    },

    async fetchUserSystemLists(userId) {
      const types = ['watchlist', 'circle', 'favourites', 'seen']
      const lists = await Promise.all(
        types.map((type) => this.fetchSystemListByTypeAndUser(type, userId))
      )
      await Promise.all(
        lists
          .filter((list) => list && list.is_stub !== true)
          .map((list) => this.fetchAllListItemsById(list.id))
      )
    },

    async fetchUserListMeta(listId, userId) {
      const { data, error } = await supabase
        .from('lists')
        .select(
          '*, profiles!fk_lists_profiles_user_id(*), list_items(count), list_review_stats(*), clubs:club_id(id, name, slug, cover_url)'
        )
        .eq('id', listId)
        .eq('user_id', userId)
        .single()

      if (error) {
        console.error(`Failed fetching use lists ${userId}, ${listId}`, error)
        return
      }

      const updatedList = {
        ...data,
        is_following: false,
        can_edit: userId === this.currentUserId,
        is_owner: userId === this.currentUserId
      }

      this.updateLists([updatedList])
    },

    async fetchUserLists(userId) {
      // Guard: Validate userId parameter
      if (!userId) {
        console.warn('fetchUserLists called with undefined userId')
        return null
      }

      const { data, error } = await supabase
        .from('lists')
        .select(
          '*, profiles!fk_lists_profiles_user_id(*), list_items(count), list_review_stats(*), clubs:club_id(id, name, slug, cover_url)'
        )
        .eq('is_system', false)
        .eq('user_id', userId)

      if (error) {
        console.error('Failed fetching user lists', error)
        return
      }

      const updatedLists = data.map((list) => ({
        ...list,
        is_following: false,
        can_edit: userId === this.currentUserId,
        is_owner: userId === this.currentUserId
      }))

      this.updateLists(updatedLists)
      this.loaded = true
      this.requestValidateLists()

      this.fetchUserFollowingLists(userId)
    },

    async fetchUserFollowingLists(userId) {
      const { data, error } = await supabase
        .from('list_follows')
        .select(
          '*, lists(*, profiles!fk_lists_profiles_user_id(*), clubs:club_id(id, name, slug, cover_url))'
        )
        .eq('user_id', userId)

      if (error) {
        console.error('Failed fetching user lists', error)
        return
      }

      const updatedLists = data.map((item) => ({
        ...item.lists,
        is_following: true,
        can_edit: false
      }))

      this.updateListsFollowing(updatedLists)
    },

    async addList(newList) {
      const { data, error } = await supabase.functions.invoke('lists', {
        body: { list: newList }
      })

      if (error) {
        return { data: null, error: error }
      }

      // Add the new list to local state so it appears immediately.
      // Use updateLists() which deduplicates by id — the realtime
      // INSERT handler may have already added this list before the
      // HTTP response arrived.
      if (data?.list) {
        this.updateLists([data.list])
      }

      // Track custom list creation in PostHog (skip system lists)
      if (import.meta.env.VITE_APP_MODE === 'production' && newList.list_type === 'custom') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('List Created', {
            is_collaborative: Boolean(newList.is_collaborative),
            is_public: Boolean(newList.public)
          })
        })
      }

      return { data: data, error: null }
    },

    async editList(listId, listData) {
      const { data, error } = await supabase.functions.invoke(
        `lists/${listId}/edit`,
        {
          body: { ...listData }
        }
      )

      if (error) {
        return { data: null, error: error }
      }

      // Optimistically update the local list with the edited fields
      const index = this.lists.findIndex((l) => l.id === listId)
      if (index !== -1) {
        Object.assign(this.lists[index], listData)
      }

      return { data: data, error: null }
    },

    // Update system list backdrop image directly (bypasses edge function restrictions)
    async updateSystemListBackdrop(listId, backdropPath) {
      const { data, error } = await supabase
        .from('lists')
        .update({ backdrop_path: backdropPath })
        .eq('id', listId)
        .eq('is_system', true)
        .eq('user_id', this.currentUserId)
        .select()
        .single()

      if (error) {
        console.error('Failed to update system list backdrop:', error)
        return { data: null, error }
      }

      // Update the local store
      if (data) {
        const index = this.lists.findIndex((list) => list.id === listId)
        if (index !== -1) {
          this.lists[index] = {
            ...this.lists[index],
            backdrop_path: backdropPath
          }
        }
      }

      return { data, error: null }
    },
    async toggleInListBySlug(listSlug, title, metadata = null) {
      // Get the list ID by the slug
      const listId = this.lists.find((list) => list.slug === listSlug).id
      if (listId === undefined) {
        console.error('toggleInListBySlug failed to find list')
        return
      }

      return this.toggleInList(listId, title, metadata)
    },

    async toggleInList(listId, title, metadata = null) {
      const path = `lists/${listId}/items`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { title: title, metadata }
      })

      if (error) {
        console.log('Error calling lists function', error)
        throw error
      }

      // Update the meta for that list
      this.fetchUserListMeta(listId, this.currentUserId)

      return data
    },

    async addToNewList(title, metadata = null) {
      const { data, error } = await this.addList({ title: 'New List' })
      if (error) {
        console.error('Error adding new list', error)
        throw error
      }
      const list = data.list
      await this.addToList(list.id, title, metadata)
      return { list, error: null }
    },

    async addToList(listId, title, metadata = null) {
      const path = `lists/${listId}/items`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { title: title, metadata }
      })

      if (error) {
        console.error('Error calling lists function', error)
        throw error
      }

      // Optimistically add the returned list_item to local state
      if (data?.list_item) {
        this.updateItems([data.list_item])
      }

      // Track list item addition in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('List Item Added', {
            title_type: title?.media_type,
            list_id: listId
          })
        })
      }

      this.fetchUserListMeta(listId, this.currentUserId)

      return data
    },

    async deleteList(listId) {
      const path = `lists/${listId}/delete`
      const { error } = await supabase.functions.invoke(path, {
        body: { listId: listId }
      })

      if (error) {
        console.error('Error calling lists function', error.message)
        throw error
      }

      // Clear the list from cached memory
      this.removeList(listId)

      // Remove all items for this list
      this.items = this.items.filter((item) => item.list_id !== listId)

      // Remove from following lists if present
      this.listsFollowing = this.listsFollowing.filter(
        (list) => list.id !== listId
      )

      // Clean up pagination state
      if (this.paginatedCursors[listId]) {
        delete this.paginatedCursors[listId]
      }
      if (this.listsPaginationState[listId]) {
        delete this.listsPaginationState[listId]
      }

      // Clean up error state
      if (this.listErrors[listId]) {
        delete this.listErrors[listId]
      }

      return true
    },

    async requestValidateLists() {
      const path = `lists/validate`
      const { error } = await supabase.functions.invoke(path, { body: {} })

      if (error) {
        console.error('Error calling lists function', error.message)
        throw error
      }

      return true
    },

    async updateListItemImage(
      listId,
      itemId,
      customImagePath,
      cropData = null
    ) {
      const path = `lists/${listId}/items/${itemId}/image`
      const body = { custom_image_path: customImagePath }

      // Add crop data if provided
      if (cropData) {
        body.crop_x = cropData.x
        body.crop_y = cropData.y
        body.crop_width = cropData.width
        body.crop_height = cropData.height
        body.crop_scale = cropData.scale || 1.0
      }

      const { data, error } = await supabase.functions.invoke(path, { body })

      if (error) {
        console.error('Error updating list item image', error)
        throw error
      }

      // Update local store
      const itemIndex = this.items.findIndex((item) => item.id === itemId)
      if (itemIndex >= 0) {
        this.items[itemIndex] = {
          ...this.items[itemIndex],
          custom_image_path: customImagePath,
          ...(cropData && {
            crop_x: cropData.x,
            crop_y: cropData.y,
            crop_width: cropData.width,
            crop_height: cropData.height,
            crop_scale: cropData.scale || 1.0
          })
        }
      }

      return data
    },

    async fetchListBySlug(listSlug, ownerId = null) {
      // Use provided ownerId or fall back to currentUserId for uniqueness
      const userId = ownerId || this.currentUserId

      if (!userId) {
        console.error(
          'fetchListBySlug requires either ownerId parameter or currentUserId to be set'
        )
        return
      }

      const { data, error } = await supabase
        .from('lists')
        .select(
          '*, profiles!fk_lists_profiles_user_id(*), list_items(count), clubs:club_id(id, name, slug, cover_url)'
        )
        .eq('slug', listSlug)
        .eq('user_id', userId)
        .single()
      if (error) {
        console.error('Failed fetching lists with slug', listSlug, error)
        return
      }

      // Client-side privacy guard: don't store private lists from other users
      if (data.is_private && data.user_id !== this.currentUserId) {
        return
      }

      this.updateLists([data])
    },

    async fetchListById(listId) {
      if (!listId) return null

      const { data, error } = await supabase
        .from('lists')
        .select(
          '*, profiles!fk_lists_profiles_user_id(*), list_items(count), clubs:club_id(id, name, slug, cover_url)'
        )
        .eq('id', listId)
        .single()

      if (error) {
        console.error('Failed fetching list by id', listId, error)
        return null
      }

      // Client-side privacy guard: don't store private lists from other users
      if (data.is_private && data.user_id !== this.currentUserId) {
        return null
      }

      this.updateLists([data])
      return data
    },

    async fetchListItemById(itemId) {
      const { data, error } = await supabase
        .from('list_items')
        .select(
          `*,titles!list_items_title_id_fkey(*), added_by_profile:profiles!list_items_added_by_profiles_fk(*), reviews(*, profiles(*)), notes(*, profiles:profiles!notes_user_id_profiles_fk(*))`
        )
        .eq('id', itemId)
        .single()

      if (error) {
        console.error('Error fetching list item by id', itemId, error)
      } else {
        this.updateItems([data])
      }

      return
    },

    async fetchListItemsBySlug(listSlug, revalidate = false, ownerId = null) {
      if (!this.initReady) {
        return new Promise((resolve) => {
          watchEffect(async () => {
            if (this.initReady) {
              const result = await this.fetchListItemsBySlug(
                listSlug,
                revalidate,
                ownerId
              )
              resolve(result)
            }
          })
        })
      }

      // Use provided ownerId or fall back to currentUserId for uniqueness
      const userId = ownerId || this.currentUserId

      if (!userId) {
        console.error(
          'fetchListItemsBySlug requires either ownerId parameter or currentUserId to be set'
        )
        if (revalidate === true) {
          // Fire off a request to the server to revalidate the list state for the user
          this.requestValidateLists()
        }
        return null
      }

      const { data: listData, error: listError } = await supabase
        .from('lists')
        .select('id, is_private, user_id')
        .eq('slug', listSlug)
        .eq('user_id', userId)
        .single() // Expecting only one list, so use .single() to return a single object

      // Client-side privacy guard: don't load items for private lists from other users
      if (listData?.is_private && listData.user_id !== this.currentUserId) {
        return null
      }

      if (listError || !listData) {
        if (revalidate === true) {
          // Fire off a request to the server to revalidate the list state for the user
          this.requestValidateLists()
        }
        console.error('List not found', listError)
        return null
      }

      const listId = listData.id // Get the list ID
      return this.fetchAllListItemsById(listId)
    },

    async fetchListItemsById(listId) {
      // Guard: Validate listId is a proper UUID
      if (!isValidUUID(listId)) {
        console.warn('Invalid list ID, skipping fetchListItemsById:', listId)
        return
      }

      // Guard: Check if this list has had a permanent error
      if (this.listErrors[listId]?.permanent) {
        return
      }

      const { data, error } = await supabase
        .from('list_items')
        .select(
          `*,titles!list_items_title_id_fkey(*), added_by_profile:profiles!list_items_added_by_profiles_fk(*), reviews(*, profiles(*)), notes(*, profiles:profiles!notes_user_id_profiles_fk(*))`
        )
        .eq('list_id', listId)
        .order('order_num', { ascending: true })
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching list items with titles:', error)
        // Mark as permanent error if it's a bad request
        if (error.code === '22P02' || error.code === '400') {
          this.listErrors[listId] = { permanent: true, error }
        }
      } else {
        this.updateItems(data)
      }

      return
    },

    async followList(listId) {
      // Fire a request to the server to follow the list
      const path = `lists/${listId}/follow`
      const { error } = await supabase.functions.invoke(path, { body: {} })

      if (error) {
        console.error('Error calling lists function', error.message)
        throw error
      }

      // Now add this list to the following lists
      this.fetchUserFollowingLists(this.currentUserId)
    },

    async unfollowList(listId) {
      // Fire a request to the server to unfollow the list
      const path = `lists/${listId}/unfollow`
      const { error } = await supabase.functions.invoke(path, { body: {} })

      if (error) {
        console.error('Error calling lists function', error.message)
        throw error
      }

      // Now remove this list from the following lists
      this.listsFollowing = this.listsFollowing.filter(
        (list) => list.id !== listId
      )
    },

    async fetchItemsByTitle(tmdbTitle) {
      // First find the title_id from the titles table
      const { data: titleData, error: titleError } = await supabase
        .from('titles')
        .select('id')
        .eq('tmdb_id', tmdbTitle.id)
        .eq('tmdb_type', tmdbTitle.type)
        .maybeSingle()

      if (titleError || !titleData) {
        // Title doesn't exist in our DB yet, no list items possible
        return
      }

      // Now fetch list items for this title_id
      const { data, error } = await supabase
        .from('list_items')
        .select(
          `*, titles!list_items_title_id_fkey(id, tmdb_id, tmdb_type, tmdb_cache, tmdb_poster_path, title), added_by_profile:profiles!list_items_added_by_profiles_fk(*), reviews(*, profiles(*)), notes(*, profiles:profiles!notes_user_id_profiles_fk(*))`
        )
        .eq('title_id', titleData.id)

      if (error) {
        console.error('Error fetching list items with titles:', error)
      } else {
        if (data && data.length > 0) {
          this.updateItems(data)
        }
      }

      return
    },

    /**
     * Fetch all public lists containing a specific title
     * @param {Object} tmdbTitle - { id, type }
     */
    async fetchListsContainingTitle(tmdbTitle) {
      if (!tmdbTitle?.id || !tmdbTitle?.type) return

      // Normalize the type to match database expectations
      // TMDB API expects 'movie' or 'tv', not 'tv_series' or other variants
      let normalizedType = tmdbTitle.type
      if (normalizedType === 'tv_series' || normalizedType === 'series') {
        normalizedType = 'tv'
      }

      const key = `${tmdbTitle.type}_${tmdbTitle.id}`
      this.titleListsLoading[key] = true

      try {
        // First find the title_id from the titles table
        const { data: titleData, error: titleError } = await supabase
          .from('titles')
          .select('id')
          .eq('tmdb_id', tmdbTitle.id)
          .eq('tmdb_type', normalizedType)
          .maybeSingle()

        if (titleError) {
          console.error('Error finding title in database:', titleError)
          this.titleLists[key] = []
          return
        }

        if (!titleData) {
          // Title doesn't exist in database yet - this is normal for titles that haven't been added to any lists
          console.log(
            `Title not found in database: tmdb_id=${tmdbTitle.id}, tmdb_type=${normalizedType} (original: ${tmdbTitle.type})`
          )
          this.titleLists[key] = []
          return
        }

        // Fetch public lists and user's own lists in parallel
        const listItemsSelect = `
            list_id,
            user_id,
            lists!inner(
              id,
              title,
              description,
              backdrop_path,
              is_private,
              is_system,
              user_id,
              created_at,
              profiles!user_id(username, avatar_url, displayname)
            )
          `

        const publicQuery = supabase
          .from('list_items')
          .select(listItemsSelect)
          .eq('title_id', titleData.id)
          .eq('lists.is_private', false)
          .eq('lists.is_system', false)

        const userQuery = this.currentUserId
          ? supabase
              .from('list_items')
              .select(listItemsSelect)
              .eq('title_id', titleData.id)
              .eq('lists.user_id', this.currentUserId)
              .eq('lists.is_system', false)
          : null

        const [publicResult, userResult] = await Promise.all([
          publicQuery,
          userQuery
        ])

        const publicData = publicResult.data
        const publicError = publicResult.error
        const userData = userResult?.data || []
        const userError = userResult?.error || null

        // Combine results
        const data = [...(publicData || []), ...userData]
        const error = publicError || userError

        if (error) {
          console.error('Error fetching lists containing title:', error)
          this.titleLists[key] = []
        } else {
          // Deduplicate lists and format
          const listsMap = new Map()
          data?.forEach((item) => {
            if (item.lists && !listsMap.has(item.lists.id)) {
              listsMap.set(item.lists.id, item.lists)
            }
          })
          const uniqueLists = Array.from(listsMap.values())
          this.titleLists[key] = uniqueLists
        }

      } catch (err) {
        console.error('Error in fetchListsContainingTitle:', err)
        this.titleLists[key] = []
      } finally {
        this.titleListsLoading[key] = false
      }
    },

    /**
     * Get lists containing a specific title
     * @param {Object} tmdbTitle - { id, type }
     */
    getListsContainingTitle(tmdbTitle) {
      if (!tmdbTitle?.id || !tmdbTitle?.type) return []
      const key = `${tmdbTitle.type}_${tmdbTitle.id}`
      return this.titleLists[key] || []
    },

    /**
     * Search lists by name and by contained titles
     * @param {string} query - Search query
     */
    async searchLists(query) {
      if (!query || query.length < 2) {
        this.searchResults = { byName: [], byTitle: [] }
        this.searchQuery = ''
        this.searchTotalCount = 0
        return
      }

      this.searchQuery = query
      this.searchLoading = true

      try {
        const storeAuth = useStoreAuth()
        const userId = storeAuth.user?.id

        // 1. Search lists by name/title (public lists + user's own lists)
        let listsByNameQuery = supabase
          .from('lists')
          .select('*, profiles!user_id(username, avatar_url, displayname)')
          .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
          .eq('is_system', false)
          .order('created_at', { ascending: false })
          .limit(20)

        // Filter: public lists OR user's own lists
        if (userId) {
          listsByNameQuery = listsByNameQuery.or(
            `is_private.eq.false,user_id.eq.${userId}`
          )
        } else {
          listsByNameQuery = listsByNameQuery.eq('is_private', false)
        }

        const { data: listsByName, error: nameError } = await listsByNameQuery

        if (nameError) {
          console.error(
            '[storeLists] Error searching lists by name:',
            nameError
          )
        }

        // 2. Search lists containing matching titles
        // First find titles matching the query
        const { data: matchingTitles, error: titlesError } = await supabase
          .from('titles')
          .select('id, title')
          .ilike('title', `%${query}%`)
          .limit(50)

        let listsByTitle = []
        if (!titlesError && matchingTitles && matchingTitles.length > 0) {
          const titleIds = matchingTitles.map((t) => t.id)

          // Find list items containing those titles
          let itemsQuery = supabase
            .from('list_items')
            .select(
              `
              list_id,
              title_id,
              lists!inner(
                id,
                title,
                description,
                slug,
                backdrop_path,
                is_private,
                user_id,
                club_id,
                created_at,
                profiles!user_id(username, avatar_url, displayname)
              )
            `
            )
            .in('title_id', titleIds)
            .eq('lists.is_system', false)
            .limit(50)

          // Filter: public lists OR user's own lists
          if (userId) {
            itemsQuery = itemsQuery.or(
              `lists.is_private.eq.false,lists.user_id.eq.${userId}`
            )
          } else {
            itemsQuery = itemsQuery.eq('lists.is_private', false)
          }

          const { data: itemsWithLists, error: itemsError } = await itemsQuery

          if (!itemsError && itemsWithLists) {
            // Deduplicate lists and attach matching title info
            const listsMap = new Map()
            itemsWithLists.forEach((item) => {
              if (item.lists && !listsMap.has(item.lists.id)) {
                const matchedTitle = matchingTitles.find(
                  (t) => t.id === item.title_id
                )
                listsMap.set(item.lists.id, {
                  ...item.lists,
                  matched_title: matchedTitle
                })
              }
            })
            listsByTitle = Array.from(listsMap.values())
          }
        }

        // Remove duplicates from byTitle that already appear in byName
        const byNameIds = new Set((listsByName || []).map((l) => l.id))
        listsByTitle = listsByTitle.filter((l) => !byNameIds.has(l.id))

        this.searchResults = {
          byName: listsByName || [],
          byTitle: listsByTitle
        }
        this.searchTotalCount = (listsByName?.length || 0) + listsByTitle.length
      } catch (error) {
        console.error('[storeLists] Search error:', error)
        this.searchResults = { byName: [], byTitle: [] }
        this.searchTotalCount = 0
      } finally {
        this.searchLoading = false
      }
    },

    // ============================================================================
    // COLLABORATIVE LIST ACTIONS
    // ============================================================================

    /**
     * Toggle collaborative mode for a list
     */
    async toggleCollaborative(listId, enabled) {
      const path = `lists/${listId}/collaborative`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { enabled }
      })

      if (error) {
        console.error('Error toggling collaborative mode:', error)
        throw error
      }

      // Update local list state
      const listIndex = this.lists.findIndex((l) => l.id === listId)
      if (listIndex !== -1) {
        this.lists[listIndex] = {
          ...this.lists[listIndex],
          is_collaborative: enabled
        }
      }

      return data
    },

    /**
     * Fetch members for a collaborative list
     */
    async fetchListMembers(listId) {
      const path = `lists/${listId}/members`
      const { data, error } = await supabase.functions.invoke(path, {
        method: 'GET'
      })

      if (error) {
        console.error('Error fetching list members:', error)
        throw error
      }

      this.collaborativeListMembers[listId] = data.members || []
      return data.members
    },

    /**
     * Leave a list (member only, not owner)
     */
    async leaveList(listId) {
      const path = `lists/${listId}/leave`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {}
      })

      if (error) {
        console.error('Error leaving list:', error)
        throw error
      }

      // Remove from members list
      if (this.collaborativeListMembers[listId]) {
        this.collaborativeListMembers[listId] = this.collaborativeListMembers[
          listId
        ].filter((m) => m.user_id !== this.currentUserId)
      }

      // Remove from joined lists
      this.listsJoinedAsMember = this.listsJoinedAsMember.filter(
        (l) => l.id !== listId
      )

      return data
    },

    /**
     * Remove a member from a list (owner only)
     */
    async removeMember(listId, memberId) {
      const path = `lists/${listId}/members/${memberId}/remove`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {}
      })

      if (error) {
        console.error('Error removing member:', error)
        throw error
      }

      // Update local members list
      if (this.collaborativeListMembers[listId]) {
        this.collaborativeListMembers[listId] = this.collaborativeListMembers[
          listId
        ].filter((m) => m.user_id !== memberId)
      }

      return data
    },

    /**
     * Fetch activity log for a list
     */
    async fetchListActivity(listId, limit = 50, offset = 0) {
      const path = `lists/${listId}/activity?limit=${limit}&offset=${offset}`
      const { data, error } = await supabase.functions.invoke(path, {
        method: 'GET'
      })

      if (error) {
        console.error('Error fetching list activity:', error)
        throw error
      }

      if (offset === 0) {
        this.listActivity[listId] = data.activity || []
      } else {
        this.listActivity[listId] = [
          ...(this.listActivity[listId] || []),
          ...(data.activity || [])
        ]
      }

      return data.activity
    },

    /**
     * Fetch lists where current user is a member (not owner)
     */
    async fetchJoinedLists() {
      const { data, error } = await supabase
        .from('list_members')
        .select(
          `
          list_id,
          role,
          joined_at,
          lists (
            id,
            title,
            description,
            slug,
            backdrop_path,
            is_collaborative,
            is_system,
            is_auto,
            is_editable,
            icon,
            created_at,
            updated_at,
            user_id,
            profiles!fk_lists_profiles_user_id(*),
            list_items (count)
          )
        `
        )
        .eq('user_id', this.currentUserId)
        .eq('status', 'active')
        .neq('role', 'owner')

      if (error) {
        console.error('Error fetching joined lists:', error)
        return []
      }

      this.listsJoinedAsMember = (data || [])
        .filter((m) => m.lists)
        .map((m) => ({
          ...m.lists,
          membership: { role: m.role, joined_at: m.joined_at }
        }))

      return this.listsJoinedAsMember
    },

    // ============================================================================
    // INVITE LINK ACTIONS
    // ============================================================================

    /**
     * Create an invite link for a collaborative list
     */
    async createInviteLink(listId, options = {}) {
      const path = `lists/${listId}/invites`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {
          max_uses: options.maxUses || null,
          expires_in_days: options.expiresInDays || 7
        }
      })

      if (error) {
        console.error('Error creating invite link:', error)
        throw error
      }

      // Override URL with domain-aware version (for localhost dev)
      const invite = {
        ...data.invite,
        url: buildInviteUrl(data.invite.invite_code)
      }

      // Update local state
      if (!this.listInviteLinks[listId]) {
        this.listInviteLinks[listId] = []
      }
      this.listInviteLinks[listId].unshift(invite)

      // Refresh list data (is_collaborative may have been enabled)
      this.fetchListById(listId)

      return invite
    },

    /**
     * Fetch all invite links for a list
     */
    async fetchInviteLinks(listId) {
      const path = `lists/${listId}/invites`
      const { data, error } = await supabase.functions.invoke(path, {
        method: 'GET'
      })

      if (error) {
        console.error('Error fetching invite links:', error)
        throw error
      }

      // Override URLs with domain-aware versions (for localhost dev)
      const invites = (data.invites || []).map((invite) => ({
        ...invite,
        url: buildInviteUrl(invite.invite_code)
      }))

      this.listInviteLinks[listId] = invites
      return invites
    },

    /**
     * Revoke an invite link
     */
    async revokeInviteLink(listId, inviteId) {
      const path = `lists/${listId}/invites/${inviteId}/revoke`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {}
      })

      if (error) {
        console.error('Error revoking invite link:', error)
        throw error
      }

      // Update local state
      if (this.listInviteLinks[listId]) {
        this.listInviteLinks[listId] = this.listInviteLinks[listId].filter(
          (invite) => invite.id !== inviteId
        )
      }

      return data
    },

    /**
     * Get invite info by code (for join page)
     */
    async getInviteInfo(inviteCode) {
      const path = `lists/invite/${inviteCode}`
      const { data, error } = await supabase.functions.invoke(path, {
        method: 'GET'
      })

      if (error) {
        console.error('Error getting invite info:', error)
        throw error
      }

      return data
    },

    /**
     * Get user invite info by invite ID (for direct user invites)
     */
    async getUserInviteInfo(inviteId) {
      const path = `lists/invites/${inviteId}`
      const { data, error } = await supabase.functions.invoke(path, {
        method: 'GET'
      })

      if (error) {
        console.error('Error getting user invite info:', error)
        throw error
      }

      return data
    },

    /**
     * Join a list via invite code
     */
    async joinViaInviteCode(inviteCode) {
      const path = `lists/join/${inviteCode}`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {}
      })

      if (error) {
        console.error('Error joining via invite code:', error)
        throw error
      }

      // Refresh joined lists
      await this.fetchJoinedLists()

      return data
    },

    // ============================================================================
    // DIRECT USER INVITE ACTIONS
    // ============================================================================

    /**
     * Invite a user directly to a list
     */
    async inviteUserToList(listId, userId) {
      const path = `lists/${listId}/invite-user`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { user_id: userId }
      })

      if (error) {
        console.error('Error inviting user to list:', error)
        throw error
      }

      // Refresh invites list to get complete data with profiles relationship
      await this.fetchUserInvites(listId)

      // Refresh list data (is_collaborative may have been enabled)
      this.fetchListById(listId)

      return data.invite
    },

    /**
     * Fetch pending user invites for a list (as owner)
     */
    async fetchUserInvites(listId) {
      // This would need to be implemented on the backend
      // For now, we can use a direct query
      const { data, error } = await supabase
        .from('list_user_invites')
        .select(
          `
          id,
          invited_user_id,
          invited_by,
          status,
          created_at,
          profiles:invited_user_id (id:user_id, user_id, username, displayname, display_name:displayname, avatar_url)
        `
        )
        .eq('list_id', listId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching user invites:', error)
        throw error
      }

      this.listUserInvites[listId] = data || []
      return data
    },

    /**
     * Cancel a user invite (as owner)
     */
    async cancelUserInvite(listId, inviteId) {
      const { error } = await supabase
        .from('list_user_invites')
        .delete()
        .eq('id', inviteId)

      if (error) {
        console.error('Error canceling user invite:', error)
        throw error
      }

      // Update local state
      if (this.listUserInvites[listId]) {
        this.listUserInvites[listId] = this.listUserInvites[listId].filter(
          (invite) => invite.id !== inviteId
        )
      }

      return true
    },

    /**
     * Fetch pending invites received by current user
     */
    async fetchPendingInvites() {
      const path = 'lists/invites/pending'
      const { data, error } = await supabase.functions.invoke(path, {
        method: 'GET'
      })

      if (error) {
        console.error('Error fetching pending invites:', error)
        throw error
      }

      this.pendingReceivedInvites = data.invites || []
      return data.invites
    },

    /**
     * Accept a user invite
     */
    async acceptUserInvite(inviteId) {
      const path = `lists/invites/${inviteId}/accept`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {}
      })

      if (error) {
        console.error('Error accepting invite:', error)
        throw error
      }

      // Remove from pending invites
      this.pendingReceivedInvites = this.pendingReceivedInvites.filter(
        (invite) => invite.id !== inviteId
      )

      // Refresh joined lists
      await this.fetchJoinedLists()

      return data
    },

    /**
     * Decline a user invite
     */
    async declineUserInvite(inviteId) {
      const path = `lists/invites/${inviteId}/decline`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {}
      })

      if (error) {
        console.error('Error declining invite:', error)
        throw error
      }

      // Remove from pending invites
      this.pendingReceivedInvites = this.pendingReceivedInvites.filter(
        (invite) => invite.id !== inviteId
      )

      return data
    },

    // ============================================================================
    // PINNED LISTS ACTIONS
    // ============================================================================

    /**
     * Pin a list to the user's profile
     * Multiple lists can be pinned, ordered by when they were pinned (newest first)
     */
    async pinList(listId) {
      const path = 'profile/updatePinnedList'
      const { data, error } = await supabase.functions.invoke(path, {
        body: { listId, action: 'pin' }
      })

      if (error) {
        console.error('Error pinning list:', error)
        throw error
      }

      // Update local state
      const list = this.lists.find((l) => l.id === listId)
      if (list) {
        list.pinned_at = new Date().toISOString()
      }

      return data
    },

    /**
     * Unpin a list from the user's profile
     */
    async unpinList(listId) {
      const path = 'profile/updatePinnedList'
      const { data, error } = await supabase.functions.invoke(path, {
        body: { listId, action: 'unpin' }
      })

      if (error) {
        console.error('Error unpinning list:', error)
        throw error
      }

      // Update local state
      const list = this.lists.find((l) => l.id === listId)
      if (list) {
        list.pinned_at = null
      }

      return data
    }
  },
  getters: {
    getPaginatedCursors: (state) => {
      return state.paginatedCursors
    },

    getSystemListByType: (state) => {
      return (type) => {
        return state.lists.find(
          (list) =>
            list.is_system === true &&
            list.system_type == type &&
            list.user_id == state.currentUserId &&
            list.is_stub !== true // Exclude stubs from getter results
        )
      }
    },

    getSystemListByTypeAndUser: (state) => {
      return (type, userId) => {
        return state.lists.find(
          (list) =>
            list.is_system === true &&
            list.system_type == type &&
            list.user_id == userId &&
            list.is_stub !== true // Exclude stubs from getter results
        )
      }
    },

    getUserLists: (state) => {
      if (!state.currentUserId) {
        return []
      }

      // Get user's own editable lists
      const ownLists = state.lists.filter((list) => {
        const basicMatch =
          list.is_editable === true && list.user_id === state.currentUserId

        // For system lists, only include watchlist and favourites (exclude seen, reviews, etc.)
        const systemMatch =
          list.is_system === false ||
          (list.is_system === true &&
            ['watchlist', 'favourites'].includes(list.system_type))

        return basicMatch && systemMatch
      })

      // Include collaborative lists user has joined (they can add items)
      const joinedLists = state.listsJoinedAsMember.filter(
        (list) => list.is_collaborative
      )

      return [...ownLists, ...joinedLists]
    },

    getListsFollowing: (state) => {
      return state.listsFollowing
    },

    isFollowingList: (state) => {
      return (listId) => {
        const found = state.listsFollowing.find((list) => list.id === listId)
        return found !== undefined
      }
    },

    getListsByUser: (state) => {
      return (user_id) => {
        const ownedLists = state.lists.filter((list) => {
          return (
            list.is_auto === false &&
            list.is_editable === true &&
            list.user_id === user_id &&
            list.is_system === false
          )
        })
        // If viewing own profile, also include joined collaborative lists
        if (user_id === state.currentUserId) {
          return [...ownedLists, ...state.listsJoinedAsMember]
        }
        return ownedLists
      }
    },
    userEditableLists: (state) => {
      return state.lists.filter((list) => list.is_editable === true)
    },
    getUserListBySlug: (state) => {
      return (slug) => state.lists.find((list) => list.slug === slug)
    },
    // Cached index: rebuilds only when state.items changes (Pinia caches
    // getters that return a value). Function-returning getters are NOT cached.
    _listItemsIndex: (state) => {
      const index = new Map()
      for (const item of state.items) {
        let bucket = index.get(item.list_id)
        if (!bucket) {
          bucket = []
          index.set(item.list_id, bucket)
        }
        bucket.push(item)
      }
      for (const bucket of index.values()) {
        bucket.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        )
      }
      return index
    },
    getListItemsByListId() {
      return (listId) => this._listItemsIndex.get(listId) || []
    },
    getListItemByListIdAndTitleId: (state) => {
      return (listId, titleId) => {
        if (!listId) return null

        return state.items.find(
          (item) => item.list_id === listId && item.title_id === titleId
        )
      }
    },
    getListItemsByTitle: (state) => {
      return (titleId) => {
        return state.items
          .filter(
            (item) =>
              item.title_id === titleId && item.user_id === state.currentUserId
          )
          .filter((item) => {
            // Exclude items from auto and system lists (watchlist, seen, favourites, circle)
            // These have their own dedicated UI indicators
            const list = state.lists.find((l) => l.id === item.list_id)
            if (!list || list.is_auto || list.is_system) return false
            return true
          })
          .filter(
            (item, index, self) =>
              self.findIndex((i) => i.list_id === item.list_id) === index
          )
      }
    },

    // const listItemIdByListId = (listId) => {
    //   if (!listId) return null
    //   const titleId = titleDbId.value
    //   const items = storeLists.items.filter(
    //     (item) => item.list_id === listId && item.title_id === titleId
    //   )
    //   return items.length > 0 ? items[0].id : null
    // }

    getListByIdOrSlug: (state) => {
      return (listIdOrSlug) => {
        // First check user's own lists
        const ownList = state.lists.find(
          (list) =>
            list !== undefined &&
            (list.id === listIdOrSlug || list.slug === listIdOrSlug)
        )
        if (ownList) return ownList

        // Also check collaborative lists user has joined
        return state.listsJoinedAsMember.find(
          (list) =>
            list !== undefined &&
            (list.id === listIdOrSlug || list.slug === listIdOrSlug)
        )
      }
    },
    autoLists() {
      return [
        this.getUserListBySlug('circle'),
        this.getUserListBySlug('reviews')
      ]
    },
    getListPaginationState: (state) => {
      return (listId) => {
        return (
          state.listsPaginationState[listId] || {
            loading: false,
            hasMore: true
          }
        )
      }
    },

    // ============================================================================
    // COLLABORATIVE LIST GETTERS
    // ============================================================================

    /**
     * Get members for a collaborative list
     */
    getListMembers: (state) => {
      return (listId) => state.collaborativeListMembers[listId] || []
    },

    /**
     * Get activity log for a list
     */
    getListActivity: (state) => {
      return (listId) => state.listActivity[listId] || []
    },

    /**
     * Check if current user is a member of a list (not owner)
     */
    isListMember: (state) => {
      return (listId) => {
        const members = state.collaborativeListMembers[listId] || []
        return members.some(
          (m) =>
            m.user_id === state.currentUserId &&
            m.role === 'member' &&
            m.status === 'active'
        )
      }
    },

    /**
     * Check if current user is the owner of a list
     */
    isListOwner: (state) => {
      return (listId) => {
        const list = state.lists.find((l) => l.id === listId)
        return list?.user_id === state.currentUserId
      }
    },

    /**
     * Check if current user can modify items in a list (owner or active member)
     */
    canModifyListItems: (state) => {
      return (listId) => {
        const list = state.lists.find((l) => l.id === listId)
        if (!list) return false

        // Owner can always modify
        if (list.user_id === state.currentUserId) return true

        // For collaborative lists, check membership
        if (list.is_collaborative) {
          const members = state.collaborativeListMembers[listId] || []
          return members.some(
            (m) => m.user_id === state.currentUserId && m.status === 'active'
          )
        }

        return false
      }
    },

    /**
     * Get lists the current user has joined as a member
     */
    getJoinedLists: (state) => {
      return state.listsJoinedAsMember
    },

    /**
     * Get member count for a list
     */
    getListMemberCount: (state) => {
      return (listId) => {
        const collaborativeMembers =
          state.collaborativeListMembers[listId] || []
        const activeMemberCount = collaborativeMembers.filter(
          (m) => m.status === 'active'
        ).length

        // Get the list to check if owner should be included
        const list =
          state.lists.find((l) => l && l.id === listId) ||
          (state.listsJoinedAsMember || []).find((l) => l && l.id === listId)

        // If we don't have list data, just return collaborative member count
        if (!list || !list.user_id) {
          return activeMemberCount
        }

        // Check if owner is already in the collaborative members list
        const ownerAlreadyInMembers = collaborativeMembers.some(
          (member) => member.user_id === list.user_id
        )

        // If owner is already counted, don't add extra count
        if (ownerAlreadyInMembers) {
          return activeMemberCount
        }

        // Add 1 for the owner
        return activeMemberCount + 1
      }
    },

    // ============================================================================
    // PINNED LISTS GETTERS
    // ============================================================================

    /**
     * Get all pinned lists for current user, ordered by pinned_at DESC (newest first)
     */
    getPinnedLists: (state) => {
      return state.lists
        .filter((list) => list.pinned_at && list.user_id === state.currentUserId)
        .sort((a, b) => new Date(b.pinned_at) - new Date(a.pinned_at))
    },

    /**
     * Get all pinned lists for a specific user, ordered by pinned_at DESC
     */
    getPinnedListsByUser: (state) => {
      return (userId) => {
        return state.lists
          .filter((list) => list.pinned_at && list.user_id === userId)
          .sort((a, b) => new Date(b.pinned_at) - new Date(a.pinned_at))
      }
    },

    /**
     * Check if a list is pinned
     */
    isListPinned: (state) => {
      return (listId) => {
        const list = state.lists.find((l) => l.id === listId)
        return !!list?.pinned_at
      }
    }
  }
})
